// @flow

import * as React from 'react';
import L10nMessage from 'react-aaui/lib/shared/L10nMessage';
import ResultCell from 'components/ResultCell/ResultCell';
import Row from 'react-aaui/lib/grid/Row';
import Col from 'components/Col/Col';
import ShowAt from 'components/Viewport/ShowAt';
import HideAt from 'components/Viewport/HideAt';
import prop from 'lodash/fp/prop';
import type { SplitResult as SplitResultType } from 'type/type';
import { formatedDuration } from 'services/format/formatDuration';
import { transKilometersAsUnit } from 'services/newton';

import StageIcon from '../../StageIcon/StageIcon';
import { getResultPace } from '../../duck/selector';
import './SplitResult.scss';

type Props = {
  result: SplitResultType,
  name: string,
  isStart: boolean,
  isTriathlon: boolean,
  isLive: boolean,
}

const formatContent = (result: SplitResultType) => {
  const splitTime = prop('splitTime')(result);
  const formattedSplitTime = splitTime && formatedDuration(splitTime);

  return formattedSplitTime && formattedSplitTime !== '00:00:00' ? formattedSplitTime : '--';
};

const SplitTimeCell = ({ result }: { result: SplitResultType }) => {
  const splitTimeContent = formatContent(result);

  return (
    <ResultCell
      label={<L10nMessage id="page.participant.splitReuslt.splitTime" />}
      content={splitTimeContent} />
  );
};

const TotalTimeCell = ({ result }: { result: SplitResultType }) => {
  return (
    <ResultCell
      label={<L10nMessage id="page.participant.splitReuslt.totalTime" />}
      content={
        result && result.totalTime
          ? formatedDuration(result.totalTime)
          : undefined
      } />
  );
};

const SplitResult = ({
  name, result, isStart, isTriathlon, stageType, isLive,
}: Props) => {
  return (
    <div className="participant-result-row split-result">
      <Row className="participant-result-row__content">
        <Col className="stage-info" span={6} sm={4}>
          <Row>
            <Col className="stage-type-section" span={4} md={3} xl={2}>
              <StageIcon type={stageType} size="24px" wrapperClass="stage-type" color="#0069cc" />
            </Col>
            <Col span={8} className="name">
              {name}
            </Col>
          </Row>
        </Col>
        <Col className="result" span={6} sm={8} xl={7}>
          { isStart || (isLive && !result) ? (
            ''
          ) : (
            <Row>
              <ShowAt smAndAbove>
                <Col className="split-time" span={4} xl={3}>
                  <SplitTimeCell result={result} />
                </Col>
                <Col className="total-time" span={4} xl={3}>
                  <TotalTimeCell result={result} />
                </Col>
              </ShowAt>
              <Col className="pace" span={12} sm={4} xl={3}>
                <HideAt smAndAbove><i className="iconfont icon-pace" /></HideAt>
                <ResultCell
                  label={
                    <ShowAt smAndAbove>
                      <L10nMessage id="page.participant.splitResult.pace" />
                    </ShowAt>
                  }
                  content={
                    result && result.speed ? <span>
                      {getResultPace({ ...result, stageType }).value}
                      <span className="unit">{getResultPace({ ...result, stageType }).unit}</span>
                    </span> : undefined
                  } />
              </Col>
              <ShowAt xlAndAbove>
                <Col className="speed" span={3}>
                  {!isTriathlon && <ResultCell
                    label={<L10nMessage id="page.participant.splitResult.speed" />}
                    content={
                      result && result.speed ? (
                        <span>
                          {transKilometersAsUnit(result.speed, result.distanceUnit)}
                          <span className="unit">
                            {' '}
                            <L10nMessage id={`app.domain.result.speed.unit.${result.distanceUnit}`} />
                          </span>
                        </span>
                      ) : undefined
                    } />}
                </Col>
              </ShowAt>
            </Row>
          )}
        </Col>
        <ShowAt xlAndAbove>
          <Col className="time" span={2} />
        </ShowAt>
      </Row>
      <HideAt smAndAbove>
        {
          isStart || (isLive && !result) ? null : (
            <Row className="race-duration--mobile">
              <Col className="split-time" span={6}>
                <SplitTimeCell result={result} />
              </Col>
              <Col className="total-time" span={6}>
                <TotalTimeCell result={result} />
              </Col>
            </Row>
          )
        }
      </HideAt>
    </div>
  );
};

export default SplitResult;
